import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { AcdCallbackDataObj } from './acd-callback.model';
import { AcdCallbackService } from './acd-callback.service';
import { ChartConfiguration } from 'chart.js';
import { resolve } from 'chart.js/helpers';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { BaseChartDirective } from 'ng2-charts';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'cv-acd-callback',
    templateUrl: './acd-callback.component.html',
    styleUrls: ['./acd-callback.component.scss']
})
export class AcdCallbackComponent implements OnInit {

    @Input() moduleKey: string;
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;
    @Input() private moduleManager: ModuleManager;

    totalLabelSize: number;
    state = null;
    isFirstLoad = true;
    data: AcdCallbackDataObj = null;
    dataLoading = false;
    dataLoadSuccess = false;
    totalCount = 0;
    // Chart.js Setup
    chartType = 'doughnut';
    chartPadding = {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
    };
    chartConfig = null;
    _chartConstants = {
        colors: [ // This list is in the order of Chart dataset backgroundColor values
            "rgba(189,32,38,1)", // Failure
            "rgba(228,179,33,1)", // Inqueue
            "rgba(61,150,73,1)", // Successful
        ],
        colorsLight: [ // This list is in the order of Chart dataset backgroundColor values
            "rgba(189,32,38,.5)", // Failure
            "rgba(228,179,33,.5)", // Inqueue
            "rgba(61,150,73,.5)", // Successful
        ],
        borderColor: 'rgba(121, 138, 146, 1)', // dark grey
        fontColor: "#353a40", // theUpsideDown color
        fontFamily: "'OpenSans-SemiBold', 'Lato-Regular', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'", // Includes fallbacks (from default ChartJS config)
        fontSize: 13,
    };


    constructor(
        private acdCallbackService: AcdCallbackService,
        private cvLocalizationService: CVLocalizationService,
        private config: CvConfigurationService,
        private datePipe: DatePipe,
    ) { }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        await this.cvLocalizationService.GetSection("Callback_Requests");

        this.moduleManager.setOptions({
            onSettingsApplied: async () => await this.loadData(false),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.VeryShort,
            onRefresh: async isAutoRefresh => { return await this.loadData(isAutoRefresh); },
            canRefresh: () => this.dataLoadSuccess,
        });

        this.moduleManager.registerEvents({
            onResize: () => this.setTotalLabelSize()
        });

        this.moduleManager.addMenuItem({
            name: "Legend_Position",
            icon: 'legend',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null,
            subItems: [{
                name: "Top", // Menu items are localized automatically by Module Container.  Keys under section Module_Menu
                icon: 'positionTop',
                click: () => this.setLegendPosition('top'),
            },
            {
                name: "Bottom",
                icon: 'positionBottom',
                click: () => this.setLegendPosition('bottom')
            },
            {
                name: "Left",
                icon: 'positionLeft',
                click: () => this.setLegendPosition('left')
            },
            {
                name: "Right",
                icon: 'positionRight',
                click: () => this.setLegendPosition('right')
            }]
        } as ModuleManagerSubMenu);

        this.moduleManager.addState({
            name: 'chartState',
            autoLoad: true,
            getStateData: () => this.state,
            onStateLoaded: (stateData) => {
                if (!stateData)
                    return;

                this.state = stateData;
            }
        });
    }

    async loadData(autoRefresh) {
        this.dataLoading = true;
        this.moduleManager.showLoader = !autoRefresh;

        if (this.state == null || this.state.legendPosition == null) {
            this.state = {
                legendPosition: 'top',
                dataPadding: '5% 0 0 0'
            };
        }

        try {
            this.data = await this.acdCallbackService.LoadData(this.moduleKey, autoRefresh, this.isFirstLoad);
            this.totalCount =  this.data.Fail + this.data.Inqueue + this.data.Success;
            this.createChart();
        }
        catch (err) {
            this.moduleManager.handleLoadError(err, autoRefresh);
            return;
        }

        this.setTotalLabelSize();
        this.dataLoadSuccess = true;
        this.moduleManager.showLoader = false;
        this.moduleManager.setAutoRefreshReady();
        this.isFirstLoad = false;
    }

    drawCenterLabel(chart) {
        if (chart.config.options.elements.center) {
            //Get ctx from string
            const ctx = chart.ctx;

            //Get options from the center object in options
            const centerConfig = chart.config.options.elements.center;
            const fontStyle = centerConfig.fontStyle || this._chartConstants.fontFamily;

            // total the shown data
            let total = 0;
            try {
                if (this.chartObject) {
                    const datasetMeta = this.chartObject.chart.getDatasetMeta(0);
                    datasetMeta.data.forEach((data, i) => {
                        if (!this.chartObject.chart.getDataVisibility(i)) { // ignore hidden data CHECK FIX
                            return;
                        }

                        const dataValue = this.getDataByIndex(i) || 0;
                        total += dataValue;
                    });
                } else {
                    total = this.totalCount;
                }

            } catch(e) { /* total will be 0 if no datasets */ }

            const metricName = total === 1 ? this.cvLocalizationService.getCachedText("Callback_Requests", "Callback", "Callback")
                : this.cvLocalizationService.getCachedText("Callback_Requests", "Callbacks", "Callbacks");
            const metricValue = total.toString();
            const color = centerConfig.color || this._chartConstants.fontColor;
            const sidePadding = centerConfig.sidePadding || 32;
            const innerRadius = chart._metasets[0].data[0].innerRadius;
            const sidePaddingCalculated = (sidePadding/100) * (innerRadius * 2);
            //Start with a base font of 30px
            ctx.font = "30px " + fontStyle;

            //Get the width of the string and also the width of the element minus 32 to give it 16px side padding
            const metricNameWidth = ctx.measureText(metricName).width;
            const elementWidth = (innerRadius * 2) - sidePaddingCalculated;

            // Find out how much the font can grow in width.
            const widthRatio2 = elementWidth / metricNameWidth;

            const newFontSize2 = Math.floor(21 * widthRatio2);
            const newFontSize = Math.floor(newFontSize2 * 1.67);

            const elementHeight = (innerRadius * 2);

            // Pick a new font size so it will not be larger than the height of label.
            const fontSizeToUse = Math.min(newFontSize, elementHeight);
            const fontSizeToUse2 = Math.min(newFontSize2, elementHeight);


            // Set font settings to draw it correctly.
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const centerX = ((chart.chartArea.left + chart.chartArea.right) / 2);
            const centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2);
            ctx.font = fontSizeToUse + "px " + fontStyle;
            ctx.fillStyle = color;

            // Draw number in center
            ctx.fillText(metricValue, centerX, centerY - (fontSizeToUse2/2));

            ctx.font = fontSizeToUse2 + "px " + fontStyle;

            // Draw Agents text in center
            ctx.fillText(metricName, centerX, centerY + fontSizeToUse2 + 5); // (fontSizeToUse2/2) + 5 is to offset slightly from center so texts stack
        }
    }

    // /**
    //  * This function calculates the normalized weights of each slice, with a minimum weight to each slice.
    //  * @param {[number, number, number, number]} dataObj Four-number array for Available, Inbound, Outbound, Unavailable weights (in that order)
    //  */
    calcWeightsWithMinSize(dataObj: AcdCallbackDataObj) {
        const threshold = 0.11; // Minimum weight of 11%
        const values = [dataObj.Fail, dataObj.Inqueue, dataObj.Success];
        const valuesSum = dataObj.Fail + dataObj.Inqueue + dataObj.Success;
        if (valuesSum <= 0)
            return [0, 0, 0];
        const normalizedValues = values.map((v) => v / valuesSum );
        const aboveThresholdIndices = []; // List of indices (of the values array) that are above the threshold.  These will be reduced when there are values below the threshold
        let aboveThresholdSum = 0; // Sum of all the values above the threshold.  This is used to figure out the proportion to reduce each value by.
        let belowThresholdDiffSum = 0; // For values below the threshold, this variable is the sum of all the (threshold - value) differences
        for (let i = 0; i < normalizedValues.length; i++) {
            if (normalizedValues[i] > 0 && normalizedValues[i] < threshold) {
                belowThresholdDiffSum += threshold - normalizedValues[i];
                normalizedValues[i] = threshold;
            } else if (normalizedValues[i] > 0 && normalizedValues[i] > threshold) {
                aboveThresholdSum += normalizedValues[i];
                aboveThresholdIndices.push(i);
            }
        }
        // Proportionally reduce weights of those above the threshold.  Larger chunks get reduced more.
        aboveThresholdIndices.forEach( i => {
            const relativeSize = normalizedValues[i] / aboveThresholdSum;
            normalizedValues[i] -= relativeSize * belowThresholdDiffSum;
        });
        return normalizedValues;
    }

    /**
     * Returns the value from ctrl.data pertaining to ChartJS's dataset index.
     * 0 = Available, 1 = Inbound, 2 = Outbound, 3 = Acw, 4 = Unavailable
     */
    getDataByIndex(index) {
        switch (index) {
            case 0:
                return this.data && this.data.Fail;
            case 1:
                return this.data && this.data.Inqueue;
            case 2:
                return this.data && this.data.Success;
            default:
                return undefined;
        }
    }

    createChart() {
        // Check for and destroy any existing charts
        if (this.state == null || this.state.legendPosition == null) {
            this.state = {
                legendPosition: 'top',
                dataPadding: '5% 0 0 0'
            };
        }

        // Check for null/empty datasets
        // If found, create an empty chart
        if (this.data === null) {
            return;
        }

        //
        // Make Chart
        //
        const preparedLabels = [
            this.cvLocalizationService.getCachedText("Callback_Requests", "Failed", "Failed"),
            this.cvLocalizationService.getCachedText("Callback_Requests", "Inqueue", "Inqueue"),
            this.cvLocalizationService.getCachedText("Callback_Requests", "Successful", "Successful")
        ];

        this.chartConfig = {
            type: 'doughnut',
            data: {
                datasets: [{
                    data: this.calcWeightsWithMinSize(this.data),
                    backgroundColor: this._chartConstants.colors,
                    borderColor: this._chartConstants.borderColor,
                    borderWidth: 1,
                    hoverBackgroundColor: this._chartConstants.colorsLight,
                }],
                labels: preparedLabels
            },
            options: {
                cutout: '75%', // makes ring thicker and thinner
                responsive: true,
                maintainAspectRatio: false,
                elements: {
                    center: {
                        text: "", // The actual content of the center is drawn by the beforeDraw custom plugin below
                        color: this._chartConstants.fontColor,
                        fontFamily: this._chartConstants.fontFamily,
                    },
                },
                plugins: {
                    emptyChart: {
                        disableCxone: this.config.isCxOne,
                    },
                    tooltip: {
                        padding: this.config.isCxOne ? 12 : 6,
                        usePointStyle: true,
                        boxPadding: this.config.isCxOne ? 3 : 1,
                        boxWidth: this.config.isCxOne ? 11 : 18,
                        boxHeight: this.config.isCxOne ? 11 : 18,
                        callbacks: {
                            title: tooltipItemArray =>
                                this.config.isCxOne ? this.datePipe.transform(new Date(), "MMM dd, yyyy") : tooltipItemArray[0].label,
                            label: tooltipItem => {
                                const numInState = this.getDataByIndex(tooltipItem.dataIndex);
                                const percent = numInState / this.totalCount * 100;
                                const precentFormat = percent.toPrecision(4);
                                return this.config.isCxOne ? tooltipItem.label : precentFormat + "%";
                            },
                            footer: tooltipItem => {
                                const numInState = this.getDataByIndex(tooltipItem[0].dataIndex);
                                const total = this.data.Fail + this.data.Inqueue + this.data.Success;
                                const percent = numInState / total * 100;
                                const precentFormat = percent.toPrecision(4);
                                const cxoneFooter = '    ' + precentFormat + '%';
                                const pmFooter = '';
                                return this.config.isCxOne ? cxoneFooter : pmFooter;
                            },
                            labelColor: context => ({
                                borderColor: 'rgb(255, 255, 255)',
                                backgroundColor: context.dataset.backgroundColor[context.dataIndex],
                                borderWidth: this.config.isCxOne ? 0 : 0.5,
                            }),
                            labelPointStyle: context => ({
                                pointStyle: this.config.isCxOne ? 'circle' : 'rect',
                                rotation: 0,
                            })
                        },
                        backgroundColor: this.config.isCxOne ? '#FFF' : 'rgba(0, 0, 0, 0.8)',
                        titleFont: {
                            size: this.config.isCxOne ? 12 : 16,
                            weight: this.config.isCxOne ? "normal" : "bold",
                        },
                        titleColor: this.config.isCxOne ? '#8c95a5' : '#fff',
                        bodyColor: this.config.isCxOne ? '#353a40' : '#fff',
                        footerColor: '#353a40',
                        bodyFont: {
                            size: 14,
                        },
                        footerFont: {
                            size: 14,
                            weight: 'bold',
                        },
                        borderColor: this.config.isCxOne ? '#D0D2D3' : '#353a40',
                        borderWidth: this.config.isCxOne ? 1 : 0,
                        cornerRadius: this.config.isCxOne ? 0 : 6,
                    },
                    legend: {
                        display: true,
                        position: this.state.legendPosition,
                        labels: {
                            boxWidth: 13,
                            font: {
                                family: this._chartConstants.fontFamily,
                                size: this._chartConstants.fontSize,
                            },
                            color: this._chartConstants.fontColor,
                            generateLabels: (chart) => { // adds custom labels to the chart legend
                                const data = chart.data;
                                if (data.labels.length && data.datasets.length) {
                                    return data.labels.map((label, i) => {
                                        const meta = chart.getDatasetMeta(0);
                                        const ds = data.datasets[0];
                                        const arc = meta.data[i];
                                        const custom = arc && arc.options || {};

                                        const arcOpts = chart.options.elements.arc;
                                        const fill = custom.backgroundColor ? custom.backgroundColor :
                                            resolve([ds.backgroundColor, arcOpts.backgroundColor], chart.ctx , i);
                                        const stroke = custom.borderColor ? custom.borderColor :
                                            resolve([ds.borderColor, arcOpts.borderColor], chart.ctx, i);
                                        const bw = custom.borderWidth ? custom.borderWidth :
                                            resolve([ds.borderWidth, arcOpts.borderWidth], chart.ctx, i);

                                        // We get the value of the current label
                                        const value = this.getDataByIndex(i);
                                        const toggle = chart.getDataVisibility(i);

                                        return {
                                            // Instead of `text: label,`
                                            // We add the value to the string
                                            text: label + " (" + value + ")",
                                            fillStyle: fill,
                                            strokeStyle: stroke,
                                            lineWidth: bw,
                                            hidden: !toggle,
                                            index: i
                                        };
                                    });
                                }
                            },
                        }
                    },
                },
                layout: {
                    padding: {
                        left: 8,
                        right: 8,
                        top: 8,
                        bottom: 8
                    }
                },
                animation: {
                    duration: 0,
                },
                hover: {
                    animationDuration: 0,
                },
            },
            plugins: [{
                id: "callback-requests-center-label",
                beforeDraw: chart => this.drawCenterLabel(chart),
            }],
        } as ChartConfiguration;
        this.chartConfig.data.datasets[0].data = this.calcWeightsWithMinSize(this.data);
    }

    async setLegendPosition(position) {
        if(this.state.legendPosition === position)
            return;

        this.state.legendPosition = position;
        this.chartConfig.options.plugins.legend.position = position;
        this.loadData(false);
        await this.moduleManager.saveState('chartState');
        this.moduleManager.requestPublishNeeded();
    }

    setTotalLabelSize() {
        this.totalLabelSize = this.chartObject?.chart.width / 9;
    }
}
